import { analytics, logEvent } from './firebase';

// Analytics utility functions for PlayFm app
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  // Make analytics completely non-blocking by using setTimeout
  setTimeout(() => {
    if (!analytics) {
      console.log('Analytics not available, would track:', eventName, parameters);
      return;
    }
    
    try {
      logEvent(analytics, eventName, parameters);
    } catch (error) {
      console.warn('Failed to track analytics event:', eventName, error);
    }
  }, 0);
};

// Audio/Stream Events
export const trackStreamStart = () => {
  trackEvent('stream_start', {
    content_type: 'audio_stream',
    content_id: 'playfm_dublin_live'
  });
};

export const trackStreamStop = () => {
  trackEvent('stream_stop', {
    content_type: 'audio_stream',
    content_id: 'playfm_dublin_live'
  });
};

export const trackStreamError = (errorType: string) => {
  trackEvent('stream_error', {
    error_type: errorType,
    content_type: 'audio_stream'
  });
};

export const trackVolumeChange = (isMuted: boolean, volume?: number) => {
  trackEvent('volume_change', {
    is_muted: isMuted,
    volume_level: volume
  });
};

export const trackCastingStart = (castType: 'airplay' | 'chromecast') => {
  trackEvent('casting_start', {
    cast_type: castType
  });
};

export const trackCastingStop = (castType: 'airplay' | 'chromecast') => {
  trackEvent('casting_stop', {
    cast_type: castType
  });
};

// Chat Events
export const trackChatOpen = () => {
  trackEvent('chat_open');
};

export const trackChatClose = () => {
  trackEvent('chat_close');
};

export const trackMessageSent = (hasReply: boolean = false, hasMentions: boolean = false) => {
  trackEvent('message_sent', {
    has_reply: hasReply,
    has_mentions: hasMentions
  });
};

export const trackReactionGiven = (reactionType: string) => {
  trackEvent('reaction_given', {
    reaction_type: reactionType
  });
};

export const trackEmojiModalOpen = () => {
  trackEvent('emoji_modal_open');
};

// Schedule Events
export const trackScheduleView = () => {
  trackEvent('schedule_view');
};

export const trackShowFavorited = (showId: string, showName: string) => {
  trackEvent('show_favorited', {
    show_id: showId,
    show_name: showName
  });
};

export const trackShowUnfavorited = (showId: string, showName: string) => {
  trackEvent('show_unfavorited', {
    show_id: showId,
    show_name: showName
  });
};

// Sleep Timer Events
export const trackSleepTimerStart = (minutes: number, isCustom: boolean = false) => {
  trackEvent('sleep_timer_start', {
    timer_minutes: minutes,
    is_custom_time: isCustom
  });
};

export const trackSleepTimerCancel = () => {
  trackEvent('sleep_timer_cancel');
};

export const trackSleepTimerComplete = () => {
  trackEvent('sleep_timer_complete');
};

// Notification Events
export const trackNotificationPermissionRequested = () => {
  trackEvent('notification_permission_requested');
};

export const trackNotificationPermissionGranted = () => {
  trackEvent('notification_permission_granted');
};

export const trackNotificationPermissionDenied = () => {
  trackEvent('notification_permission_denied');
};

export const trackNotificationSettingsChanged = (settingType: string, enabled: boolean) => {
  trackEvent('notification_settings_changed', {
    setting_type: settingType,
    enabled: enabled
  });
};

export const trackNotificationReceived = (notificationType: string) => {
  trackEvent('notification_received', {
    notification_type: notificationType
  });
};

// App Install/PWA Events
export const trackInstallPromptShown = () => {
  trackEvent('install_prompt_shown');
};

export const trackInstallPromptAccepted = () => {
  trackEvent('install_prompt_accepted');
};

export const trackInstallPromptDismissed = () => {
  trackEvent('install_prompt_dismissed');
};

// Navigation Events
export const trackPageView = (pageName: string) => {
  trackEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href
  });
};

export const trackMenuOpen = () => {
  trackEvent('menu_open');
};

export const trackMenuClose = () => {
  trackEvent('menu_close');
};

// Social Media Events
export const trackSocialMediaClick = (platform: 'facebook' | 'instagram' | 'mixcloud') => {
  trackEvent('social_media_click', {
    platform: platform
  });
};

export const trackShareClick = () => {
  trackEvent('share_click');
};

// Listening Stats Events
export const trackListeningStatsView = () => {
  trackEvent('listening_stats_view');
};

// Error Events
export const trackError = (errorType: string, errorMessage?: string) => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage
  });
};

// User Engagement Events
export const trackUserEngagement = (engagementType: 'first_interaction' | 'session_start' | 'return_visit') => {
  trackEvent('user_engagement', {
    engagement_type: engagementType
  });
};

// Connection Events
export const trackConnectionStatusChange = (isOnline: boolean) => {
  trackEvent('connection_status_change', {
    is_online: isOnline
  });
}; 